/**
 * Specific-goal helpers shared by Progress, onboarding, and Arnold context.
 * Stored in existing user_goals columns — no schema change:
 *   running: exercise_name = race name, target_value = distance,
 *            target_unit = km|miles, description = target minutes,
 *            target_date = race day, current_value = current best minutes
 */

export type GoalType = 'strength' | 'endurance' | 'running' | 'weight';

export const RACE_PRESETS = [
  { id: '5k', name: '5K', km: 5, defaultMinutes: 25 },
  { id: '10k', name: '10K', km: 10, defaultMinutes: 48 },
  { id: '15k', name: '15K', km: 15, defaultMinutes: 75 },
  { id: 'half', name: 'Half Marathon', km: 21.1, defaultMinutes: 110 },
  { id: 'marathon', name: 'Marathon', km: 42.2, defaultMinutes: 240 },
  { id: 'custom', name: 'Custom / named race', km: 0, defaultMinutes: 45 },
] as const;

export const STRENGTH_PRESETS = ['Bench Press', 'Squat', 'Deadlift', 'Overhead Press', 'Barbell Row', 'Pull-up'] as const;

export const ENDURANCE_PRESETS = [
  { name: 'Pull-ups', unit: 'reps' },
  { name: 'Push-ups', unit: 'reps' },
  { name: 'Plank', unit: 'minutes' },
  { name: 'Dips', unit: 'reps' },
] as const;

export type RacePresetId = (typeof RACE_PRESETS)[number]['id'];

export function distanceToKm(distance: number, unit?: string | null): number {
  const u = (unit || 'km').toLowerCase();
  if (u === 'miles' || u === 'mi' || u === 'mile') return distance * 1.609344;
  return distance;
}

/**
 * Infer a preset from stored distance only. Never from the race name —
 * "Stockholm Marathon" at 8 km is custom, not the marathon chip.
 */
export function presetIdForDistanceKm(
  distance: number,
  _name?: string | null,
  unit?: string | null,
): RacePresetId {
  const km = distanceToKm(distance, unit);
  if (!(km > 0.05)) return 'custom';
  const match = RACE_PRESETS.find((p) => p.id !== 'custom' && Math.abs(p.km - km) < 0.25);
  return match?.id ?? 'custom';
}

export function formatGoalClock(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  const totalSec = Math.round(minutes * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function parseClockToMinutes(raw: string): number {
  const t = raw.trim();
  if (!t) return 0;
  const parts = t.split(':').map((p) => parseInt(p, 10) || 0);
  if (parts.length === 3) return parts[0] * 60 + parts[1] + parts[2] / 60;
  if (parts.length === 2) return parts[0] + parts[1] / 60;
  return parseFloat(t) || 0;
}

export function daysUntil(dateIso: string | null | undefined, now = new Date()): number | null {
  if (!dateIso) return null;
  const end = new Date(`${dateIso}T12:00:00`);
  if (Number.isNaN(end.getTime())) return null;
  return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Same idea as a lift: progress is how much of the gap from *your starting
 * number* to the target you have closed.
 *
 * 10K 40:00 now, 38:00 goal, started at 40:00 → 0% (nothing shaved yet)
 * After a 39:00 → 50%. Hit 38:00 → 100%.
 */
export function gapProgressPercent(args: {
  current: number;
  target: number;
  starting?: number | null;
  lowerIsBetter: boolean;
}): number {
  const current = Number(args.current);
  const target = Number(args.target);
  if (!Number.isFinite(current) || !Number.isFinite(target)) return 0;

  if (args.lowerIsBetter) {
    if (!(target > 0) || !(current > 0)) return 0;
    const start = args.starting != null && args.starting > target ? args.starting : current;
    if (current <= target) return 100;
    if (current >= start) return 0;
    const total = start - target;
    if (!(total > 0)) return 0;
    return Math.min(99, Math.max(0, ((start - current) / total) * 100));
  }

  const start =
    args.starting != null && Number.isFinite(args.starting) && args.starting !== current
      ? args.starting
      : 0;
  if (current >= target && target > 0) return 100;
  if (!(target > start)) return current >= target ? 100 : 0;
  if (current <= start) return 0;
  return Math.min(99, Math.max(0, ((current - start) / (target - start)) * 100));
}

export function runningTimeProgressPercent(args: {
  targetMinutes: number;
  currentMinutes: number;
  startingMinutes?: number | null;
}): number {
  return gapProgressPercent({
    current: args.currentMinutes,
    target: args.targetMinutes,
    starting: args.startingMinutes,
    lowerIsBetter: true,
  });
}

export function remainingGap(args: {
  current: number;
  target: number;
  lowerIsBetter: boolean;
  unit?: string;
  isTime?: boolean;
}): string | null {
  const { current, target, lowerIsBetter, unit, isTime } = args;
  if (!(target > 0) || !(current > 0)) return null;
  if (lowerIsBetter) {
    const gap = current - target;
    if (gap <= 0) return 'Goal hit';
    if (isTime) return `${formatGoalClock(gap)} to go`;
    return `${formatNum(gap)} ${unit || ''} to go`.trim();
  }
  const gap = target - current;
  if (gap <= 0) return 'Goal hit';
  if (isTime) return `${formatGoalClock(gap)} to go`;
  return `${formatNum(gap)} ${unit || ''} to go`.trim();
}

export function goalDisplay(goal: {
  goal_type?: string | null;
  exercise_name?: string | null;
  description?: string | null;
  target_value: number;
  current_value: number;
  starting_value?: number | null;
  target_unit?: string | null;
}): {
  currentLabel: string;
  targetLabel: string;
  remaining: string | null;
  typeLabel: string;
} {
  const type = String(goal.goal_type || '').toLowerCase();
  const unit = goal.target_unit || '';
  if (type === 'running') {
    const targetMin = parseFloat(String(goal.description || '0')) || 0;
    return {
      typeLabel: 'Running',
      currentLabel: goal.current_value > 0 ? formatGoalClock(goal.current_value) : '—',
      targetLabel: targetMin > 0 ? formatGoalClock(targetMin) : `${goal.target_value} ${unit}`,
      remaining: remainingGap({
        current: goal.current_value,
        target: targetMin,
        lowerIsBetter: true,
        isTime: true,
      }),
    };
  }
  const isTime = type === 'endurance' && (unit === 'minutes' || unit === 'min');
  const lowerIsBetter =
    type === 'weight' && (goal.starting_value ?? goal.current_value) > goal.target_value;
  return {
    typeLabel: type === 'strength' ? 'Strength' : type === 'endurance' ? 'Endurance' : type === 'weight' ? 'Weight' : type,
    currentLabel: isTime ? formatGoalClock(goal.current_value) : `${goal.current_value} ${unit}`.trim(),
    targetLabel: isTime ? formatGoalClock(goal.target_value) : `${goal.target_value} ${unit}`.trim(),
    remaining: remainingGap({
      current: goal.current_value,
      target: goal.target_value,
      lowerIsBetter,
      unit,
      isTime,
    }),
  };
}

function formatNum(n: number): string {
  if (!Number.isFinite(n)) return '';
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

export function formatCoachGoalLine(g: {
  goal_type?: string | null;
  exercise_name?: string | null;
  description?: string | null;
  target_value?: number | null;
  current_value?: number | null;
  starting_value?: number | null;
  target_unit?: string | null;
  target_date?: string | null;
  created_at?: string | null;
  progress_percentage?: number | null;
}): string {
  const type = String(g.goal_type || '').toLowerCase();
  const name = g.exercise_name || 'Goal';
  const days = daysUntil(g.target_date);
  const dateBit = g.target_date
    ? `, race/goal date ${g.target_date}${days != null ? ` (${days} days ${days >= 0 ? 'away' : 'ago'})` : ''}`
    : '';
  if (type === 'running') {
    const dist = g.target_value;
    const unit = g.target_unit || 'km';
    const targetMin = parseFloat(String(g.description || '0')) || 0;
    const currentMin = Number(g.current_value) || 0;
    const startMin = Number(g.starting_value) || currentMin;
    const gap = targetMin > 0 && currentMin > 0 ? currentMin - targetMin : 0;
    const pct = runningTimeProgressPercent({
      targetMinutes: targetMin,
      currentMinutes: currentMin,
      startingMinutes: startMin,
    });
    const timeBit = targetMin > 0
      ? ` target ${formatGoalClock(targetMin)}, current best ${currentMin > 0 ? formatGoalClock(currentMin) : 'not logged'}${
          gap > 0 ? ` (${formatGoalClock(gap)} still to shave)` : currentMin > 0 && currentMin <= targetMin ? ' (goal time hit)' : ''
        }. Progress ${Math.round(pct)}% = share of the gap from their STARTING best to the goal time — not calendar time.`
      : ' (distance/race — time target not set)';
    return `RUNNING RACE GOAL: ${name} — ${dist} ${unit}${timeBit}${dateBit}. This is a primary training north star. Plan long runs, weekly volume, and race-pace work toward this event. Do not treat it as a gym lift.`;
  }
  if (type === 'strength') {
    return `STRENGTH GOAL: ${name} — now ${g.current_value} → ${g.target_value} ${g.target_unit || 'kg'}${dateBit}.`;
  }
  if (type === 'endurance') {
    return `ENDURANCE GOAL: ${name} — now ${g.current_value} → ${g.target_value} ${g.target_unit || ''}${dateBit}.`;
  }
  if (type === 'weight') {
    return `BODYWEIGHT GOAL: ${name} — now ${g.current_value} → ${g.target_value} ${g.target_unit || 'kg'}${dateBit}.`;
  }
  return `${name}: ${g.current_value} → ${g.target_value} ${g.target_unit || ''}${dateBit}.`;
}
